import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Header from "@/components/Header";
import { ArrowLeft, Shield } from "lucide-react";

export default function PrivacyPolicyPage() {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchPrivacyPolicy = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await axios.get(`${API_URL}/api/privacy/${slug}`);
        setData(response.data);
      } catch (err) {
        setError(err.response?.data?.detail || "Privacy policy not found");
      } finally {
        setLoading(false);
      }
    };

    fetchPrivacyPolicy();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-white">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-slate-400">Loading privacy policy...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 text-white">
        <Header />
        <div className="mx-auto max-w-4xl px-6 py-16">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-400">Privacy Policy Not Found</h1>
            <p className="mt-4 text-slate-400">{error}</p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <Header />
      
      <div className="mx-auto max-w-4xl px-6 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link
            to={`/apps/${slug}`}
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to {data.name}
          </Link>
          
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-8 h-8 text-cyan-400" />
            <div>
              <h1 className="text-3xl font-bold">{data.name} - Privacy Policy</h1>
            </div>
          </div>
        </div>

        {/* Privacy Policy Content */}
        <div className="prose prose-invert prose-slate max-w-none">
          <div className="bg-slate-900/50 rounded-2xl border border-white/10 p-8">
            <div className="whitespace-pre-wrap text-slate-300 leading-relaxed">
              {data.privacyPolicyText}
            </div>
          </div>
        </div>

        {/* Footer Link */}
        <div className="mt-12 pt-8 border-t border-white/10 text-center">
          <Link
            to={`/apps/${slug}`}
            className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Return to {data.name} App Page
          </Link>
        </div>
      </div>
    </div>
  );
}
